/**
 * Optional campus list overrides for schools where Central's
 * GET /branches?school_id=X is incomplete.
 *
 * BUG-mentor-school-branch-roster-dps-01: DPS interim Bangalore override was
 * removed 2026-07-21 after Ritika fixed school↔branch linkage. Keep the merge
 * helpers so a temporary entry can be added again without UI rewrites.
 *
 * Keys are school_id (string). Values are extra { id, branch_name } rows.
 */
export const SCHOOL_BRANCH_OVERRIDES = {
  // empty — Central branch API is source of truth
};

/**
 * Merge Central school-scoped branches with any interim overrides.
 * Override rows are appended only when their id is not already present.
 *
 * @param {string|number} schoolId
 * @param {Array<{ id: string|number, branch_name?: string }>} apiBranches
 * @returns {Array<{ id: number|string, branch_name: string }>}
 */
export function mergeSchoolBranches(schoolId, apiBranches = []) {
  const base = Array.isArray(apiBranches) ? [...apiBranches] : [];
  const extras = SCHOOL_BRANCH_OVERRIDES[String(schoolId)] || [];
  if (extras.length === 0) return base;

  const seen = new Set(base.map((b) => String(b.id)));
  for (const extra of extras) {
    if (seen.has(String(extra.id))) continue;
    base.push({ id: extra.id, branch_name: extra.branch_name });
    seen.add(String(extra.id));
  }
  return base;
}

/** Resolve a branch label from API list + overrides (for school card display). */
export function resolveBranchLabel(schoolId, branchId, branches = []) {
  if (branchId == null || branchId === '') return null;
  const bid = String(branchId);
  const fromApi = (branches || []).find((b) => String(b.id) === bid);
  if (fromApi) return fromApi.branch_name || fromApi.name || `Branch #${bid}`;
  const fromOverride = (SCHOOL_BRANCH_OVERRIDES[String(schoolId)] || []).find(
    (b) => String(b.id) === bid
  );
  if (fromOverride) return fromOverride.branch_name || `Branch #${bid}`;
  return `Branch #${bid}`;
}

/** UI-preference key shared by SchoolsList (last campus pick per school). */
export const SCHOOL_BRANCH_UI_PREF_KEY = 'mentorSchoolBranchUIPref';

/** Read last-picked branch_id for a school from localStorage (UI pref only). */
export function readPreferredBranchId(schoolId) {
  try {
    const raw = localStorage.getItem(SCHOOL_BRANCH_UI_PREF_KEY);
    const map = raw ? JSON.parse(raw) : {};
    const id = map[schoolId] ?? map[String(schoolId)];
    return id != null && id !== '' ? id : null;
  } catch {
    return null;
  }
}

/**
 * Attach campus fields so school detail / cohort builder can show the branch.
 * Prefer explicit fields on `school`, then current selection, then UI pref + branch list.
 */
export function withSchoolBranchContext(school, { selectedSchool = null, branches = [] } = {}) {
  if (!school) return school;
  const sameSelection =
    selectedSchool && String(selectedSchool.id) === String(school.id) ? selectedSchool : null;
  const branchId =
    school.branch_id ??
    school.branchId ??
    sameSelection?.branch_id ??
    sameSelection?.branchId ??
    readPreferredBranchId(school.id);
  if (branchId == null || branchId === '') {
    return { ...school };
  }
  const branchName =
    school.branch_name ||
    school.branchName ||
    sameSelection?.branch_name ||
    sameSelection?.branchName ||
    resolveBranchLabel(school.id, branchId, branches);
  return {
    ...school,
    branch_id: branchId,
    branch_name: branchName,
  };
}
